<script lang="ts">
  import Button from '../common/Button.svelte';
  import ProgressBar from '../common/ProgressBar.svelte';
  import Badge from '../common/Badge.svelte';
  import {
    getChecklistStatus,
    dismissChecklist,
  } from '../../lib/onboardingState';

  let isCollapsed = $state(false);
  let checklistData = $state(getChecklistStatus());

  // Reactive update when state changes
  function refreshChecklist() {
    checklistData = getChecklistStatus();
  }

  // Update checklist data periodically or on visibility change
  $effect(() => {
    const interval = setInterval(refreshChecklist, 2000);
    return () => clearInterval(interval);
  });

  function toggleCollapse() {
    isCollapsed = !isCollapsed;
  }

  function handleDismiss() {
    dismissChecklist();
    // Force a UI update by dispatching a custom event
    window.dispatchEvent(new CustomEvent('onboarding-checklist-dismissed'));
  }

  function navigateTo(link: string) {
    // Update the hash directly to trigger navigation
    window.location.hash = link;
  }
</script>

<div class="checklist-container" class:collapsed={isCollapsed}>
  <div class="checklist-header">
    <div class="header-left">
      <div class="header-icon">
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
          ></path>
        </svg>
      </div>
      <div class="header-text">
        <h3 class="checklist-title">Getting Started Checklist</h3>
        <p class="checklist-subtitle">
          {#if checklistData.allComplete}
            All tasks complete! You're ready to go.
          {:else}
            {checklistData.items.filter((i) => i.completed).length} of {checklistData.items.length} completed
          {/if}
        </p>
      </div>
    </div>

    <div class="header-actions">
      <button class="collapse-button" onclick={toggleCollapse} type="button" aria-label={isCollapsed ? 'Expand checklist' : 'Collapse checklist'}>
        <svg
          class="collapse-icon"
          class:rotated={isCollapsed}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 9l-7 7-7-7"
          ></path>
        </svg>
      </button>
      <button class="dismiss-button" onclick={handleDismiss} type="button" aria-label="Dismiss checklist">
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          ></path>
        </svg>
      </button>
    </div>
  </div>

  {#if !isCollapsed}
    <div class="checklist-body">
      <ul class="checklist-items">
        {#each checklistData.items as item (item.id)}
          <li class="checklist-item" class:completed={item.completed}>
            <button
              class="item-button"
              onclick={() => !item.completed && navigateTo(item.link)}
              disabled={item.completed}
              type="button"
            >
              <div class="item-checkbox">
                {#if item.completed}
                  <svg class="check-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="3"
                      d="M5 13l4 4L19 7"
                    ></path>
                  </svg>
                {:else}
                  <div class="empty-checkbox"></div>
                {/if}
              </div>
              <div class="item-content">
                <h4 class="item-title">{item.title}</h4>
              </div>
            </button>
          </li>
        {/each}
      </ul>

      {#if checklistData.allComplete}
        <div class="completion-message">
          <div class="completion-icon">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
          </div>
          <h4 class="completion-title">Great work!</h4>
          <p class="completion-text">
            You've completed all the onboarding tasks. Feel free to explore more features or dismiss this checklist.
          </p>
          <Button variant="primary" size="sm" onclick={handleDismiss}>
            Dismiss Checklist
          </Button>
        </div>
      {/if}
    </div>
  {/if}
</div>

<style>
  .checklist-container {
    background: linear-gradient(135deg, var(--color-primary-light) 0%, var(--color-background) 100%);
    border: 1px solid var(--color-primary-border);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-md);
    overflow: hidden;
    margin-bottom: var(--spacing-xl);
    transition: all var(--transition-base);
  }

  .checklist-container.collapsed {
    background: var(--color-background-secondary);
  }

  .checklist-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--spacing-md) var(--spacing-lg);
    cursor: pointer;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: var(--spacing-md);
    flex: 1;
  }

  .header-icon {
    width: 24px;
    height: 24px;
    color: var(--color-primary);
    flex-shrink: 0;
  }

  .header-icon svg {
    width: 100%;
    height: 100%;
  }

  .header-text {
    flex: 1;
  }

  .checklist-title {
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
    margin: 0 0 2px 0;
  }

  .checklist-subtitle {
    font-size: var(--font-size-xs);
    color: var(--color-text-secondary);
    margin: 0;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
  }

  .collapse-button,
  .dismiss-button {
    background: none;
    border: none;
    cursor: pointer;
    padding: var(--spacing-sm);
    border-radius: var(--radius-md);
    color: var(--color-text-tertiary);
    transition: all var(--transition-fast);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .collapse-button:hover,
  .dismiss-button:hover {
    background-color: var(--color-background-secondary);
    color: var(--color-text-primary);
  }

  .collapse-icon {
    width: 20px;
    height: 20px;
    transition: transform var(--transition-base);
  }

  .collapse-icon.rotated {
    transform: rotate(-180deg);
  }

  .dismiss-button svg {
    width: 18px;
    height: 18px;
  }

  .checklist-body {
    padding: 0 var(--spacing-lg) var(--spacing-lg);
  }

  .checklist-items {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: var(--spacing-md);
  }

  .checklist-item {
    display: flex;
  }

  .item-button {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacing-sm);
    padding: var(--spacing-md);
    background-color: var(--color-background);
    border: 2px solid var(--color-border);
    border-radius: var(--radius-md);
    cursor: pointer;
    transition: all var(--transition-fast);
    text-align: center;
  }

  .item-button:not(:disabled):hover {
    border-color: var(--color-primary);
    background-color: var(--color-primary-light);
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }

  .item-button:disabled {
    cursor: default;
    opacity: 0.7;
    border-color: var(--color-success-border);
    background-color: var(--color-success-light);
  }

  .item-checkbox {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
  }

  .check-icon {
    width: 100%;
    height: 100%;
    color: var(--color-success);
  }

  .empty-checkbox {
    width: 100%;
    height: 100%;
    border: 2px solid var(--color-border-medium);
    border-radius: var(--radius-md);
    background-color: var(--color-background);
  }

  .item-content {
    flex: 1;
  }

  .item-title {
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    color: var(--color-text-primary);
    margin: 0;
    line-height: var(--line-height-tight);
  }

  .completion-message {
    text-align: center;
    padding: var(--spacing-2xl);
    background-color: var(--color-success-light);
    border: 1px solid var(--color-success-border);
    border-radius: var(--radius-lg);
    margin-top: var(--spacing-xl);
  }

  .completion-icon {
    width: 48px;
    height: 48px;
    margin: 0 auto var(--spacing-md);
    color: var(--color-success);
  }

  .completion-icon svg {
    width: 100%;
    height: 100%;
  }

  .completion-title {
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
    margin: 0 0 var(--spacing-sm) 0;
  }

  .completion-text {
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
    margin: 0 0 var(--spacing-lg) 0;
    line-height: var(--line-height-relaxed);
  }

  @media (max-width: 768px) {
    .checklist-items {
      grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
      gap: var(--spacing-sm);
    }

    .item-button {
      padding: var(--spacing-sm);
    }

    .item-checkbox {
      width: 28px;
      height: 28px;
    }

    .item-title {
      font-size: var(--font-size-xs);
    }
  }
</style>
